SNIPPETS

<template>
    <div 
        :id="'spl-rating-picker-' + id"
        class="d-flex justify-content-start align-items-center"
    >
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        <div class="w-100 d-flex justify-content-start align-items-center">
            <b-form-radio-group
                :checked="scoreLocal"
                @change="handleScoreSelection"
                :options="finalOptions"
                buttons
                button-variant="outline-input"
                size="sm"
                class="spl-rating-picker flex-grow-1 shadow-sm"
                :class="['score-' + scoreString, ratingTypeClass]"
            >
            </b-form-radio-group>
            <b-button
                v-if="!disableNoScore"
                :class="{active: noScore}"
                variant="outline-input"
                size="sm"
                class="ml-2"
                @click="handleNoScoreSelection"
            >
                N/A
            </b-button>
            <div style="min-width: 40px;">
                <b-button
                    v-if="isSet"
                    variant="bare-secondary"
                    size="sm"
                    @click="resetValue"
                >
                    <b-icon icon="arrow-counterclockwise"></b-icon>
                </b-button>
            </div>
            
        </div>
        <!--
        <div>
            <b-button-group 
                size="sm" 
                class="spl-rating-picker"
                :class="'score-' + scoreString"
            >
                <b-button
                    v-for="option in finalOptions"
                    :key="'score-option-' + option.value.toString()"
                    variant="outline-input"
                >{{ option.text }}</b-button>
            </b-button-group>
        </div>
        -->
    </div>
</template>

<script>
import uniqueId from 'lodash/uniqueId';

export default {
    name: 'BaseRatingPicker',
    components: {},
    props: {
        score: {  // can accomodate boolean ratings or number based ones
            type: [Boolean, Number],
            default: null,
        },
        noScore: {  // set to TRUE if specifically selected N/A, etc.  this takes priority over score value
            type: Boolean,
            default: false,
        },
        ratingType: {  // options are 'FIVE' or 'BINARY' - can add more
            // if you don't pass this, you must pass options array
            type: String,
            required: false,
        },
        options: { // pass Array of objects with text/value keys - values should be what you want to store in backend DB (0-100)
            // REQUIRED if ratingType is not set
            type: Array,
            required: false,
        },
        id: {
            type: String,
            default: function() {  // this works nicely... gotta make sure to put lodash uniqueId inside a function
                return uniqueId();
            },
        },
        locked: {  // if true, cannot edit, display only - NOT IMPLEMENTED YET
            type: Boolean,
            default: false,
        },
        disableNoScore: {  // if true, it hides 'N/A' as an option
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error with this rating - please contact support',
            error: false,
        }
    },
    computed: {
        scoreLocal: function() {
            if (this.noScore) {
                return null;
            }
            return this.score; // can't do normal ternary thing here
        },
        noScoreLocal: function() {
            return this.noScore;
        },
        isSet: function() {
            // boolean for whether it has a value set 
            return (this.score != null) || (this.noScore);
        },
        ratingTypeClass: function() {  // used to generate class name for button group FOR RATING TYPE, so you can change button widths, etc.
            if (this.ratingType) {
                return 'rating-type-' + this.ratingType.toLowerCase();
            }
            return '';
        },
        scoreString: function() { // used to generate class name for button group FOR CURRENT SCORE, so you can change button colors in CSS, etc.
            return (this.scoreLocal != null) ? this.scoreLocal.toString() : 'empty';
        },
        finalOptions: function() {
            let theOptions = [];
            if (this.options) {
                theOptions = this.options;  // if options prop is set, use that
            }
            if (this.ratingType === 'FIVE') {
                theOptions = [
                    { text: 'Poor', value: 0, },
                    { text: 'Below Avg', value: 25, },
                    { text: 'Average', value: 50, },
                    { text: 'Good', value: 75, },
                    { text: 'Excellent', value: 100, },
                ];
            }
            if (this.ratingType === 'BINARY') {
                theOptions = [
                    { text: 'Not Met', value: 0, },
                    { text: 'Requirement Met', value: 100, },
                ];
            }
            return theOptions;
        },
    },
    methods: {
        emitScore: function(scoreValue, noScoreBoolean) {
            // emit multiple things related to score (score, noScore, etc.)
            // WARNING... USE @CHANGE EVENTS ON INPUTS THAT CALL THIS, OTHERWISE IT WILL GET CALLED MULTIPLE TIMES IF YOU USE @INPUT EVENTS
            // @CHANGE EVENTS PREVENT THAT, ONLY CALLING THE EVENT WHICH IS CHANGED BY USER
            this.$emit('score-update', scoreValue);
            this.$emit('no-score-update', noScoreBoolean);
        },
        resetValue: function() {
            // reset all things... doing this will likely make score invalid on parent, but that's whats intended I think
            this.emitScore(null, false);
        },
        handleScoreSelection: function(scoreValue) {
            // if a score is selected, that means N/A should be unselected and score selected
            // no need to set local data... just emit events and let parent circle back with updated values
            this.emitScore(scoreValue, false);
        },
        handleNoScoreSelection: function() {
            // assume that ANY click on N/A button means N/A should be selected and score should be set to null
            // therefore original state of this doesn't matter
            this.emitScore(null, true);
        },
    }
}
</script>

<style>
.spl-rating-picker.btn-group.rating-type-five .btn {
    min-width: 80px;
}
.spl-rating-picker.btn-group.rating-type-binary .btn {
    min-width: 150px;
}

.spl-rating-picker.rating-type-binary.btn-group.score-0 .btn.active {
    background-color: var(--danger-100) !important;
}
.spl-rating-picker.rating-type-binary.btn-group.score-100 .btn.active {
    background-color: var(--success-100) !important;
}

.spl-rating-picker.rating-type-five.btn-group.score-0 .btn.active {
    background-color: var(--danger-200) !important;
}
.spl-rating-picker.rating-type-five.btn-group.score-25 .btn.active {
    background-color: var(--danger-100) !important;
}
.spl-rating-picker.rating-type-five.btn-group.score-50 .btn.active {
    background-color: var(--warning-100) !important;
}
.spl-rating-picker.rating-type-five.btn-group.score-75 .btn.active {
    background-color: var(--success-100) !important;
}
.spl-rating-picker.rating-type-five.btn-group.score-100 .btn.active {
    background-color: var(--success-200) !important;
}

</style>